import React from 'react';

/**
 * Base skeleton block. Uses Tailwind's built-in `animate-pulse`,
 * so no extra CSS / config is needed.
 */
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`animate-pulse rounded-xl bg-slate-200/80 ${className}`} />
);

/** Rows placeholder for tables / lists (e.g. active deals). */
export const TableRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div role="status" aria-busy="true" className="flex flex-col gap-3 py-2">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="flex items-center gap-4">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="hidden h-4 w-24 sm:block" />
        <Skeleton className="hidden h-4 w-20 md:block" />
        <Skeleton className="ml-auto h-6 w-20 rounded-full" />
      </div>
    ))}
  </div>
);

/** Placeholder for the money projection panel of the selected event. */
export const MoneyProjectionSkeleton: React.FC = () => (
  <div role="status" aria-busy="true" className="flex w-full flex-col gap-4">
    <div className="flex flex-col gap-2">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-3 w-16" />
    </div>
    <Skeleton className="h-20 w-full rounded-2xl" />
    <Skeleton className="h-14 w-full rounded-2xl" />
    <Skeleton className="h-40 w-full rounded-2xl" />
  </div>
);

/** Placeholder for the whole "Events" card (event list + money projection). */
export const EventsPanelSkeleton: React.FC = () => (
  <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-4 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04)] sm:p-6">
    <div className="grid grid-cols-12 gap-6 lg:gap-8">
      <div className="col-span-12 flex flex-col gap-4 md:col-span-4 lg:col-span-3 lg:w-60">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-9 w-full rounded-2xl" />
        <div className="hidden flex-col gap-3 md:flex">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3">
              <Skeleton className="size-9 shrink-0 rounded-2xl" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-span-12 min-w-0 md:col-span-8 lg:col-span-9">
        <MoneyProjectionSkeleton />
      </div>
    </div>
  </div>
);
